import React, { useState, useEffect } from "react";
import {
  Grid,
  TextField,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableRow,
  Tooltip,
  TableHead,
  IconButton,
  Button,
  Autocomplete,
  Dialog,
  DialogTitle,
  DialogContent,
  useMediaQuery,
  useTheme,
  Card,
  CardContent,
  Typography,
} from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  GetNextGoodsOutNo,
  createIssue,
  ApproveSolistapi,
  getDivisionUnits,
  updateIssue,
  getCategories,
  fetchSubCategories,
  fetchItemsByCategoryAndSubcategory,
  getSizes,
} from "../../../Services/Services.jsx";
import AddItemForm from "../Inventory/ItemForm";
import { useNavigate, useLocation } from "react-router-dom";
import { useCallback } from "react";
import { debounce } from "lodash";
import { CircularProgress } from "@mui/material";

const IssueForm = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));

  const getSelectedDivision = () => {
    try {
      const stored = localStorage.getItem("selectedDivision");
      return stored ? JSON.parse(stored) : null;
    } catch (e) {
      console.error("Failed to parse selectedDivision:", e);
      return null;
    }
  };
  const storedDivision = getSelectedDivision();

  const [unitOptions, setUnitOptions] = useState([]);
  const [projectOptions, setProjectOptions] = useState([]);
  const [projectPage, setProjectPage] = useState(1);
  const [projectLoading, setProjectLoading] = useState(false);
  const [projectSearch, setProjectSearch] = useState("");
  const [itemOptions, setItemOptions] = useState({});
  const [subcategories, setSubcategories] = useState([]);
  const [categories, setCategories] = useState([]);
  const [size, setSizes] = useState([]);
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [openItemModal, setOpenItemModal] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const [formErrors, setFormErrors] = useState({});
  const [rowErrors, setRowErrors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const issueData = location.state?.issue || null;
  // ✅ Fixed: Use issue_id instead of id for edit detection
  const isEditMode = !!issueData?.issue_id;

  const [formData, setFormData] = useState({
    go_no: "",
    slip_no: "",
    labour_name: "",
    division: storedDivision?.division_id || "",
    division_name: storedDivision?.division || "",
    remarks: "",
    project: "",
    created_by: localStorage.getItem("user_id"),
  });

  // Create empty row function (includes rate)
  function createEmptyRow() {
    return {
      category: null,
      sub_category: null,
      item_description: null,
      uom: "",
      stock_qty: "0",
      issue_qty: "0",
      price: "0",
      amount: "0",
      size: null,
    };
  }

  const [rows, setRows] = useState([createEmptyRow()]);

  // Helper to find UOM symbol from unit options
  const resolveUomSymbol = (item, units) => {
    if (!item) return "";

    if (item.unit !== undefined && item.unit !== null) {
      const foundUnit = units?.find((u) => String(u.id) === String(item.unit));
      if (foundUnit?.symbol) return String(foundUnit.symbol);
    }

    if (item.uom !== undefined && item.uom !== null) {
      const foundById = units?.find((u) => String(u.id) === String(item.uom));
      if (foundById?.symbol) return String(foundById.symbol);

      const foundBySymbol = units?.find(
        (u) => u.symbol === item.uom || u.name === item.uom
      );
      if (foundBySymbol?.symbol) return String(foundBySymbol.symbol);

      if (typeof item.uom === "string") return item.uom;
    }

    return "";
  };

  // Fetch dropdown data
  useEffect(() => {
    const fetchDropdowns = async () => {
      try {
        const [division_units, catRes, sizeData] = await Promise.all([
          getDivisionUnits(),
          getCategories(),
          getSizes(),
        ]);

        const units = division_units.units || [];
        const catsData = Array.isArray(catRes) ? catRes : catRes?.results || [];
        const sizesData = Array.isArray(sizeData) ? sizeData : sizeData?.results || [];

        setUnitOptions(units);
        setCategories(catsData);
        setSizes(sizesData);
        setIsDataLoaded(true);

       

        if (isEditMode && issueData) {
          setFormData({
            go_no: issueData.go_no || "",
            slip_no: issueData.slip_no || "",
            labour_name: issueData.labour_name || "",
            division: storedDivision?.division_id || issueData.division || "",
            division_name: storedDivision?.division || issueData.division_name || "",
            remarks: issueData.remarks || "",
            project: issueData.project || "",
            created_by: issueData.created_by || localStorage.getItem("user_id"),
          });

          const processItems = async () => {
            const mappedItems = await Promise.all(
              (issueData.items || []).map(async (item, index) => {
                const foundCategory = item.category
                  ? catsData.find((c) => String(c.id) === String(item.category))
                  : null;

                const foundSubCategory = item.sub_category
                  ? { subcat_id: item.sub_category, name: item.sub_category_name || "" }
                  : null;

                let itemList = [];
                if (item.category) {
                  try {
                    const itemData = await fetchItemsByCategoryAndSubcategory(
                      item.category,
                      item.sub_category || 0,
                    );
                    itemList = itemData.results || [];
                    setItemOptions((prev) => ({
                      ...prev,
                      [index]: itemList.map((i) => ({
                        id: i.id,
                        name: i.name,
                        unit_name: i.uom || i.unit_name || "",
                        current_stock: i.current_stock,
                        size_id: i.size || null,
                        size_name: i.size_name || "",
                        price: i.price || 0,
                      })),
                    }));
                  } catch (error) {
                    console.error("Error fetching items:", error);
                  }
                }

                const foundItem = item.item && itemList.length > 0
                  ? itemList.find((i) => String(i.id) === String(item.item))
                  : null;

                const foundSize = item.size && sizesData.length > 0
                  ? sizesData.find((s) => String(s.id) === String(item.size))
                  : null;

                const resolvedUom = resolveUomSymbol(item, units);
                const initialRate = item.price || foundItem?.price || "0";
                const initialAmount = (parseFloat(initialRate) * parseFloat(item.issue_qty || 0)).toFixed(2);

                return {
                  category: foundCategory || {
                    id: item.category,
                    name: item.category_name || "",
                  },
                  sub_category: foundSubCategory,
                  item_description: foundItem
                    ? {
                        id: foundItem.id,
                        name: foundItem.name,
                        unit_name: foundItem.unit_name || resolvedUom,
                        current_stock: foundItem.current_stock || item.stock_qty,
                        size_id: foundItem.size_id,
                        size_name: foundItem.size_name,
                        price: foundItem.price || item.price || 0,
                      }
                    : {
                        id: item.item,
                        name: item.item_description || "",
                        unit_name: resolvedUom,
                        current_stock: item.stock_qty,
                        size_id: item.size,
                        size_name: "",
                        price: item.price || 0,
                      },
                  uom: resolvedUom,
                  stock_qty: item.stock_qty || "0",
                  issue_qty: item.issue_qty || "0",
                  price: initialRate,
                  amount: initialAmount,
                  size: foundSize || null,
                };
              })
            );

            setRows(mappedItems.length > 0 ? mappedItems : [createEmptyRow()]);
          };

          processItems();
        } else {
          fetchNextGoodsOutNo();
        }
      } catch (err) {
        toast.error("Failed to load dropdowns");
      }
    };

    fetchDropdowns();
  }, [isEditMode, issueData]);

  // Fetch next goods out number for new issue
  const fetchNextGoodsOutNo = async () => {
    try {
      const data = await GetNextGoodsOutNo();
      if (data && data.go_no) {
        setFormData((prev) => ({
          ...prev,
          go_no: data.go_no,
        }));
      }
    } catch (err) {
      toast.error("Failed to fetch Goods Out No");
    }
  };

  // Fetch projects based on division
  const fetchProjects = async (page = 1, filters = {}, divisionId = "") => {
    try {
      setProjectLoading(true);
      const storedDivision = getSelectedDivision();
      const divId = storedDivision?.division_id;

      const finalFilters = divId ? { ...filters, division: divId } : filters;

      const response = await ApproveSolistapi(page, finalFilters);
      const results = Array.isArray(response.results)
        ? response.results
        : response || [];

      if (page === 1) {
        setProjectOptions(results);
      } else {
        setProjectOptions((prev) => [...prev, ...results]);
      }

      setProjectPage(page);
    } catch (err) {
      toast.error("Failed to load project list");
    } finally {
      setProjectLoading(false);
    }
  };

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      fetchProjects(1, { project_name: projectSearch });
    }, 500);

    return () => clearTimeout(delayDebounce);
  }, [projectSearch]);

  // Handle search input changes with debounce
  const handleSearchInputChange = useCallback(
    debounce(async (index, type, row, newValue) => {
      if (!newValue || newValue.trim() === "") {
        if (type === "category") {
          const catRes = await getCategories();
          setCategories(Array.isArray(catRes) ? catRes : catRes?.results || []);
        }
        if (type === "sub_category" && row.category) {
          const subcats = await fetchSubCategories(row.category.id);
          setSubcategories((prev) => {
            const updated = [...prev];
            updated[index] = subcats;
            return updated;
          });
        }
        if (type === "item" && row.category) {
          const itemData = await fetchItemsByCategoryAndSubcategory(
            row.category.id,
            0,
            1,
            "",
          );
          setItemOptions((prev) => ({
            ...prev,
            [index]: itemData.results || [],
          }));
        }
        if (type === "size") {
          const sizeData = await getSizes();
          setSizes(
            Array.isArray(sizeData) ? sizeData : sizeData?.results || [],
          );
        }
        return;
      }

      if (type === "category") {
        const catRes = await getCategories(1, newValue);
        setCategories(Array.isArray(catRes) ? catRes : catRes?.results || []);
      }

      if (type === "sub_category" && row.category) {
        const subcats = await fetchSubCategories(row.category.id, 1, newValue);
        setSubcategories((prev) => {
          const updated = [...prev];
          updated[index] = subcats;
          return updated;
        });
      }

      if (type === "item" && row.category) {
        const itemData = await fetchItemsByCategoryAndSubcategory(
          row.category.id,
          0,
          1,
          newValue,
        );
        setItemOptions((prev) => ({
          ...prev,
          [index]: itemData.results || [],
        }));
      }

      if (type === "size") {
        const sizeData = await getSizes(newValue || "");
        const list = Array.isArray(sizeData.results)
          ? sizeData.results
          : sizeData;
        setSizes(list);
      }
    }, 500),
    [],
  );

  // Helper to update amount and optionally row state
  const updateAmountForRow = (row) => {
    const rateNum = parseFloat(row.price) || 0;
    const qtyNum = parseFloat(row.issue_qty) || 0;
    const amount = (rateNum * qtyNum).toFixed(2);
    return { ...row, amount };
  };

  // Handle input changes in rows
  const handleInputChange = async (index, key, value) => {
    const updatedRows = [...rows];
    let row = { ...updatedRows[index] };

    switch (key) {
      case "category":
        row = {
          ...row,
          category: value,
          item_description: null,
          uom: "",
          stock_qty: "0",
          issue_qty: "0",
          price: "0",
          amount: "0",
          size: null,
        };
        if (value) {
          const itemData = await fetchItemsByCategoryAndSubcategory(
            value.id,
            0,
            page,
            searchTerm,
          );
          const mappedItems = (itemData.results || []).map((item) => ({
            id: item.id,
            name: item.name,
            unit_name: item.uom || item.unit_name,
            current_stock: item.current_stock || 0,
            size_id: item.size || null,
            size_name: item.size_name || "",
            price: item.price || 0,
          }));

          setItemOptions((prev) => ({
            ...prev,
            [index]: mappedItems,
          }));
        }
        break;

      case "item_description":
        const newRow = { ...row };
        newRow.item_description = value;

        if (value?.unit_name) {
          const unit = unitOptions.find(
            (u) => u.name === value.unit_name || u.symbol === value.unit_name,
          );
          newRow.uom = String(unit?.symbol || unit?.name || value.unit_name || "");
        }

        newRow.stock_qty = value?.current_stock ?? "0";
        newRow.price = value?.price !== undefined ? String(value.price) : "0";

        if (value?.size_id && !newRow.size) {
          const foundSize = size.find(
            (s) =>
              String(s.id) === String(value.size_id) ||
              s.name === value.size_name ||
              s.size === value.size_name,
          );
          if (foundSize) {
            newRow.size = foundSize;
          }
        }

        newRow.issue_qty = "0";
        const amountAfterItem = (parseFloat(newRow.price) * 0).toFixed(2);
        newRow.amount = amountAfterItem;

        if (parseFloat(newRow.stock_qty) === 0) {
          toast.warning(`Stock is unavailable for "${value?.name}"`);
        }

        row = newRow;
        break;

      case "issue_qty":
        const issueQty = parseFloat(value) || 0;
        const stockQty = parseFloat(row.stock_qty) || 0;

        if (issueQty > stockQty) {
          toast.error(
            `Issue Qty cannot be greater than Stock Qty in row ${index + 1}`,
          );
          return;
        }
        row.issue_qty = value;
        const newAmount = (parseFloat(row.price || 0) * issueQty).toFixed(2);
        row.amount = newAmount;
        break;

      case "price":
        const priceVal = parseFloat(value) || 0;
        row.price = value;
        const qtyVal = parseFloat(row.issue_qty) || 0;
        row.amount = (priceVal * qtyVal).toFixed(2);
        break;

      case "size":
        row = { ...row, size: value };
        break;

      case "uom":
        row = { ...row, uom: value };
        break;

      default:
        row[key] = value;
        break;
    }

    updatedRows[index] = row;
    setRows(updatedRows);
  };

  const handlePriceChange = (index, value) => {
    handleInputChange(index, "price", value);
  };

  const handleFormChange = async (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setFormErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const handleAddRow = () => {
    setRows([...rows, createEmptyRow()]);
  };

  const handleDeleteRow = (index) => {
    const updatedRows = [...rows];
    updatedRows.splice(index, 1);
    setRows(updatedRows);
  };

  const validateForm = () => {
    const errors = {};
    const rowErrs = [];

    if (!formData.slip_no) errors.slip_no = "Slip No. is required";
    if (!formData.labour_name) errors.labour_name = "Department is required";
    if (!formData.project) errors.project = "Project is required";

    rows.forEach((row, i) => {
      const err = {};
      if (!row.category) err.category = "Required";
      if (!row.item_description) err.item_description = "Required";
      if (!row.uom) err.uom = "Required";
      if (!row.issue_qty || parseFloat(row.issue_qty) <= 0)
        err.issue_qty = "Must be greater than 0";
      if (row.price === undefined || row.price === "")
        err.price = "Price is required";
      rowErrs[i] = err;
    });

    setFormErrors(errors);
    setRowErrors(rowErrs);

    return (
      Object.keys(errors).length === 0 &&
      rowErrs.every((e) => Object.keys(e).length === 0)
    );
  };

  const getTotalAmount = () => {
    return rows.reduce((sum, row) => sum + (parseFloat(row.amount) || 0), 0).toFixed(2);
  };

  const handleSubmit = async () => {
    const storedDivision = getSelectedDivision();

    if (!validateForm()) {
      toast.error("Please fill all required fields correctly");
      return;
    }

    setLoading(true);

    try {
      const itemsPayload = rows
        .map((row) => {
          const existingItem =
            isEditMode &&
            issueData?.items?.find(
              (item) => String(item.item) === String(row.item_description?.id),
            );

          let foundUnit = unitOptions.find(
            (u) => u.symbol === row.uom || u.name === row.uom
          );

          if (!foundUnit && row.uom) {
            foundUnit = unitOptions.find(
              (u) => String(u.id) === String(row.uom)
            );
          }

          const uomId = foundUnit?.id || existingItem?.unit || null;

          return {
            item: row.item_description?.id || existingItem?.item,
            category: row.category?.id || existingItem?.category || null,
            sub_category:
              row.sub_category?.subcat_id || existingItem?.sub_category || null,
            item_description:
              row.item_description?.name || existingItem?.item_description || "",
            uom: uomId,
            stock_qty: parseFloat(row.stock_qty || 0),
            issue_qty: parseFloat(row.issue_qty || 0),
            price: parseFloat(row.price || 0),
            amount: parseFloat(row.amount || 0),
            size: row.size?.id || existingItem?.size || null,
            remarks: row.remarks || "",
          };
        })
        .filter((item) => item.item);

      const payload = {
        slip_no: formData.slip_no,
        created_by: parseInt(formData.created_by),
        division: storedDivision?.division_id || formData.division || "",
        remarks: formData.remarks,
        project: formData.project,
        labour_name: formData.labour_name,
        items: itemsPayload,
        total_amount: parseFloat(getTotalAmount()),
      };

      if (isEditMode) {
        payload.go_no = issueData?.go_no;
      } else {
        try {
          const goNoData = await GetNextGoodsOutNo();
          if (goNoData?.go_no) {
            payload.go_no = goNoData.go_no;
          }
        } catch (e) {
          console.error("Failed to fetch fresh go_no:", e);
        }
      }

      let res;
      if (isEditMode) {
        res = await updateIssue(issueData.issue_id, payload);
        toast.success("Issue updated successfully!");
        navigate("/dashboard/issue-list", {
          state: { refresh: true },
          replace: true,
        });
      } else {
        res = await createIssue(payload);
        toast.success("Issue created successfully!");

        setFormData({
          go_no: "",
          slip_no: "",
          labour_name: "",
          project: "",
          remarks: "",
          created_by: localStorage.getItem("user_id"),
        });

        setRows([createEmptyRow()]);
        fetchNextGoodsOutNo();

        navigate("/dashboard/issue-list", {
          state: { refresh: true },
          replace: true,
        });
      }
    } catch (err) {
      const errData = err?.response?.data;
      console.error("Submit error:", errData);
      if (errData?.go_no) {
        toast.error(`Issue No: ${errData.go_no[0]}`);
      } else if (Array.isArray(errData)) {
        errData.forEach((itemErr, index) => {
          if (typeof itemErr === "object") {
            Object.entries(itemErr).forEach(([field, messages]) => {
              const msg = Array.isArray(messages) ? messages[0] : messages;
              toast.error(`Row ${index + 1} - ${field}: ${msg}`);
            });
          }
        });
      } else if (errData?.items) {
        toast.error(`Items Error: ${JSON.stringify(errData.items)}`);
      } else if (errData?.message) {
        toast.error(errData.message);
      } else if (errData?.detail) {
        toast.error(errData.detail);
      } else if (typeof errData === "object" && errData !== null) {
        const firstKey = Object.keys(errData)[0];
        const firstError = errData[firstKey];
        const msg = Array.isArray(firstError) ? firstError[0] : firstError;
        toast.error(`${firstKey}: ${msg}`);
      } else {
        toast.error("Submission failed. Please check the form.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCloseItemModal = async () => {
    setOpenItemModal(false);
  };

  // Helper to merge selected value into options to prevent disappearing on search
  const mergeSelectedIntoOptions = (options, selectedValue) => {
    const opts = Array.isArray(options) ? options : [];
    if (selectedValue && !opts.some(opt => opt?.id === selectedValue?.id)) {
      return [selectedValue, ...opts];
    }
    return opts;
  };

  return (
    <Box p={2}>
      {/* Responsive Buttons - Mobile Stack / Desktop Row */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          gap: 2,
          mb: 2,
          flexDirection: isMobile ? "column" : "row",
        }}
      >
        <Button
          variant="outlined"
          onClick={() => navigate(-1)}
          sx={{
            borderRadius: 0,
            height: 35,
            minWidth: isMobile ? "100%" : 120,
            fontSize: "0.7rem",
            textTransform: "none",
          }}
        >
          ← Back
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={loading}
          sx={{
            borderRadius: 0,
            height: 35,
            minWidth: isMobile ? "100%" : 120,
            fontSize: "0.7rem",
            textTransform: "none",
          }}
        >
          {loading ? (
            <CircularProgress size={22} color="inherit" />
          ) : (
            isEditMode ? "Update" : "Submit"
          )}
        </Button>
      </Box>

      <ToastContainer position="top-right" autoClose={3000} />

      <Box
        border="1px solid #ccc"
        borderRadius="8px"
        p={2}
        sx={{ backgroundColor: "#fff" }}
      >
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              label="Issue note No."
              name="go_no"
              value={formData.go_no || "Auto Generated"}
              disabled
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              label="Req Slip No"
              required
              fullWidth
              value={formData.slip_no}
              onChange={(e) => handleFormChange("slip_no", e.target.value)}
              error={!!formErrors.slip_no}
              helperText={formErrors.slip_no}
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              label="Department"
              required
              fullWidth
              value={formData.labour_name}
              onChange={(e) => handleFormChange("labour_name", e.target.value)}
              error={!!formErrors.labour_name}
              helperText={formErrors.labour_name}
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Autocomplete
              options={projectOptions}
              getOptionLabel={(opt) => opt.project_name || ""}
              value={
                Array.isArray(projectOptions)
                  ? projectOptions.find((opt) => opt.id === formData.project) ||
                    null
                  : null
              }
              loading={projectLoading}
              onChange={(e, newVal) => {
                handleFormChange("project", newVal?.id || "");
              }}
              onInputChange={(e, newInputValue) => {
                setProjectSearch(newInputValue);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  required
                  fullWidth
                  label="Project"
                  error={!!formErrors.project}
                  helperText={formErrors.project}
                  size="small"
                />
              )}
              sx={{ "& .MuiInputBase-root": { height: 40 } }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              label="Remarks"
              fullWidth
              value={formData.remarks}
              onChange={(e) => handleFormChange("remarks", e.target.value)}
              error={!!formErrors.remarks}
              helperText={formErrors.remarks}
              size="small"
            />
          </Grid>
        </Grid>

        {/* Items Section */}
        <Box mt={3}>
          {isMobile ? (
            // Mobile view (Cards)
            <Box>
              {rows.map((row, index) => (
                <Card key={index} sx={{ mb: 2, border: "1px solid #ddd" }}>
                  <CardContent>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        mb: 2,
                      }}
                    >
                      <Typography variant="h6">Item #{index + 1}</Typography>
                      <IconButton
                        onClick={() => handleDeleteRow(index)}
                        color="error"
                        size="small"
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Box>

                    <Box
                      sx={{ display: "flex", flexDirection: "column", gap: 2 }}
                    >
                      {/* Category */}
                      <Autocomplete
                        options={mergeSelectedIntoOptions(categories, row.category)}
                        getOptionLabel={(option) => option.name || ""}
                        value={row.category || null}
                        onChange={async (e, val, reason) => {
                          if (reason === "clear") {
                            handleInputChange(index, "category", null);
                            setSubcategories((prev) => {
                              const newSubcategories = [...prev];
                              newSubcategories[index] = [];
                              return newSubcategories;
                            });
                          }
                          handleInputChange(index, "category", val);
                        }}
                        onInputChange={(e, newValue, reason) => {
                          if (reason === "input") {
                            handleSearchInputChange(
                              index,
                              "category",
                              row,
                              newValue,
                            );
                          }
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Category"
                            size="small"
                            fullWidth
                            required
                            error={!!rowErrors[index]?.category}
                            helperText={rowErrors[index]?.category}
                          />
                        )}
                      />

                      {/* Item */}
                      <Autocomplete
                        options={mergeSelectedIntoOptions(itemOptions[index] || [], row.item_description)}
                        getOptionLabel={(option) => option.name || ""}
                        isOptionEqualToValue={(option, value) =>
                          option?.id === value?.id
                        }
                        value={row.item_description || null}
                        onChange={async (e, newVal, reason) => {
                          if (reason === "clear") {
                            await handleSearchInputChange(
                              index,
                              "item",
                              row,
                              "",
                            );
                            handleInputChange(index, "item_description", null);
                          }
                          handleInputChange(index, "item_description", newVal);
                        }}
                        onInputChange={(e, newValue, reason) => {
                          if (reason === "input") {
                            handleSearchInputChange(
                              index,
                              "item",
                              row,
                              newValue,
                            );
                          }
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            required
                            label="Item"
                            variant="outlined"
                            size="small"
                            fullWidth
                            error={!!rowErrors[index]?.item_description}
                            helperText={rowErrors[index]?.item_description}
                          />
                        )}
                      />

                      {/* Size */}
                      <Autocomplete
                        options={mergeSelectedIntoOptions(size, row.size)}
                        getOptionLabel={(opt) => opt?.size || opt?.name || ""}
                        isOptionEqualToValue={(option, value) =>
                          option?.id === value?.id
                        }
                        value={row.size || null}
                        onChange={(e, val, reason) => {
                          if (reason === "clear" || !val) {
                            handleInputChange(index, "size", null);
                            return;
                          }
                          handleInputChange(index, "size", val);
                        }}
                        onInputChange={(e, newValue, reason) => {
                          if (reason === "input") {
                            handleSearchInputChange(
                              index,
                              "size",
                              row,
                              newValue,
                            );
                          }
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Product Type"
                            size="small"
                            fullWidth
                          />
                        )}
                      />

                      {/* UOM */}
                      <Autocomplete
                        options={unitOptions}
                        getOptionLabel={(option) => String(option.symbol || option.name || "")}
                        isOptionEqualToValue={(option, value) =>
                          String(option?.symbol) === String(value?.symbol)
                        }
                        value={(() => {
                          if (!row.uom) return null;
                          let found = unitOptions.find((opt) => opt.symbol === row.uom);
                          if (found) return found;
                          found = unitOptions.find((opt) => String(opt.id) === String(row.uom));
                          if (found) return found;
                          if (typeof row.uom === "string" && row.uom.trim() !== "") {
                            return { symbol: row.uom, name: row.uom };
                          }
                          return null;
                        })()}
                        onChange={(e, newVal) => {
                          handleInputChange(
                            index,
                            "uom",
                            newVal?.symbol || newVal?.name || "",
                          );
                          const updated = [...rowErrors];
                          updated[index] = { ...updated[index], uom: "" };
                          setRowErrors(updated);
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            required
                            size="small"
                            label="UOM"
                            error={!!rowErrors[index]?.uom}
                            helperText={rowErrors[index]?.uom}
                          />
                        )}
                      />

                      {/* Stock Qty */}
                      <TextField
                        label="Stock Qty"
                        type="number"
                        fullWidth
                        size="small"
                        value={row.stock_qty || "0"}
                        disabled
                        InputProps={{
                          inputProps: { min: 0 },
                        }}
                      />

                      {/* Issue Qty */}
                      <TextField
                        label="Issue Qty"
                        type="number"
                        required
                        fullWidth
                        size="small"
                        value={row.issue_qty || "0"}
                        onChange={(e) => {
                          handleInputChange(index, "issue_qty", e.target.value);
                          const updated = [...rowErrors];
                          updated[index] = { ...updated[index], issue_qty: "" };
                          setRowErrors(updated);
                        }}
                        error={!!rowErrors[index]?.issue_qty}
                        helperText={rowErrors[index]?.issue_qty}
                        InputProps={{
                          inputProps: { min: 0 },
                        }}
                      />

                      {/* Rate */}
                      <TextField
                        label="Rate"
                        type="number"
                        required
                        fullWidth
                        size="small"
                        value={row.price || "0"}
                        onChange={(e) => {
                          handlePriceChange(index, e.target.value);
                          const updated = [...rowErrors];
                          updated[index] = { ...updated[index], price: "" };
                          setRowErrors(updated);
                        }}
                        error={!!rowErrors[index]?.price}
                        helperText={rowErrors[index]?.price}
                        InputProps={{
                          inputProps: { min: 0, step: "any" },
                        }}
                      />

                      {/* Amount */}
                      <TextField
                        label="Amount"
                        type="number"
                        fullWidth
                        size="small"
                        value={row.amount || "0"}
                        disabled
                        InputProps={{
                          inputProps: { min: 0, step: "any" },
                        }}
                      />
                    </Box>
                  </CardContent>
                </Card>
              ))}
              {/* Total Amount for Mobile */}
              {rows.length > 0 && (
                <Card sx={{ mt: 2, bgcolor: "#f5f5f5" }}>
                  <CardContent>
                    <Typography variant="h6" align="right">
                      Total Amount: ₹ {parseFloat(getTotalAmount()).toLocaleString()}
                    </Typography>
                  </CardContent>
                </Card>
              )}
            </Box>
          ) : (
            // Desktop view (Table)
            <>
              <TableContainer>
                <Table
                  size="small"
                  sx={{ "& td, & th": { textAlign: "center", padding: "8px" } }}
                >
                  <TableHead>
                    <TableRow
                      sx={{
                        backgroundColor: "#f5f5f5",
                        "& th": {
                          fontWeight: "bold",
                          color: "#333",
                          borderBottom: "1px solid #999",
                          backgroundColor: "#f5f5f5",
                        },
                      }}
                    >
                      <TableCell>Sr. No.</TableCell>
                      <TableCell>Category</TableCell>
                      <TableCell>Item</TableCell>
                      <TableCell>Product Type</TableCell>
                      <TableCell>UOM</TableCell>
                      <TableCell>Stock Qty.</TableCell>
                      <TableCell>Issue Qty</TableCell>
                      <TableCell>Rate</TableCell>
                      <TableCell>Amount</TableCell>
                      <TableCell>Delete</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.map((row, index) => (
                      <TableRow key={index}>
                        <TableCell>{index + 1}</TableCell>
                        <TableCell>
                          <Autocomplete
                            options={mergeSelectedIntoOptions(categories, row.category)}
                            getOptionLabel={(option) => option.name || ""}
                            value={row.category || null}
                            onChange={async (e, val, reason) => {
                              if (reason === "clear") {
                                handleInputChange(index, "category", null);
                                setSubcategories((prev) => {
                                  const newSubcategories = [...prev];
                                  newSubcategories[index] = [];
                                  return newSubcategories;
                                });
                              }
                              handleInputChange(index, "category", val);
                            }}
                            onInputChange={(e, newValue, reason) => {
                              if (reason === "input") {
                                handleSearchInputChange(
                                  index,
                                  "category",
                                  row,
                                  newValue,
                                );
                              }
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Category"
                                size="small"
                                sx={{ width: "180px" }}
                                required
                                error={!!rowErrors[index]?.category}
                                helperText={rowErrors[index]?.category}
                              />
                            )}
                          />
                        </TableCell>
                        <TableCell>
                          <Autocomplete
                            options={mergeSelectedIntoOptions(itemOptions[index] || [], row.item_description)}
                            getOptionLabel={(option) => option.name || ""}
                            isOptionEqualToValue={(option, value) =>
                              option?.id === value?.id
                            }
                            value={row.item_description || null}
                            onChange={async (e, newVal, reason) => {
                              if (reason === "clear") {
                                await handleSearchInputChange(
                                  index,
                                  "item",
                                  row,
                                  "",
                                );
                                handleInputChange(index, "item_description", null);
                              }
                              handleInputChange(index, "item_description", newVal);
                            }}
                            onInputChange={(e, newValue, reason) => {
                              if (reason === "input") {
                                handleSearchInputChange(
                                  index,
                                  "item",
                                  row,
                                  newValue,
                                );
                              }
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                required
                                label="Item"
                                variant="outlined"
                                size="small"
                                error={!!rowErrors[index]?.item_description}
                                helperText={rowErrors[index]?.item_description}
                                sx={{ width: "180px" }}
                              />
                            )}
                          />
                        </TableCell>
                        <TableCell>
                          <Autocomplete
                            options={mergeSelectedIntoOptions(size, row.size)}
                            getOptionLabel={(opt) => opt?.size || opt?.name || ""}
                            isOptionEqualToValue={(option, value) =>
                              option?.id === value?.id
                            }
                            value={row.size || null}
                            onChange={(e, val, reason) => {
                              if (reason === "clear" || !val) {
                                handleInputChange(index, "size", null);
                                return;
                              }
                              handleInputChange(index, "size", val);
                            }}
                            onInputChange={(e, newValue, reason) => {
                              if (reason === "input") {
                                handleSearchInputChange(
                                  index,
                                  "size",
                                  row,
                                  newValue,
                                );
                              }
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Product Type"
                                size="small"
                                sx={{ width: "130px" }}
                              />
                            )}
                          />
                        </TableCell>
                        <TableCell>
                          <Autocomplete
                            options={unitOptions}
                            getOptionLabel={(option) =>
                              option.symbol || option.name || ""
                            }
                            isOptionEqualToValue={(option, value) =>
                              option?.symbol === value?.symbol
                            }
                            value={
                              unitOptions.find((opt) => opt.symbol === row.uom) ||
                              (row.uom ? { symbol: row.uom, name: row.uom } : null)
                            }
                            onChange={(e, newVal) => {
                              handleInputChange(
                                index,
                                "uom",
                                newVal?.symbol || newVal?.name || "",
                              );
                              const updated = [...rowErrors];
                              updated[index] = { ...updated[index], uom: "" };
                              setRowErrors(updated);
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                required
                                size="small"
                                label="UOM"
                                error={!!rowErrors[index]?.uom}
                                helperText={rowErrors[index]?.uom}
                                sx={{ width: "130px" }}
                              />
                            )}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            type="number"
                            value={row.stock_qty || "0"}
                            disabled
                            size="small"
                            sx={{ width: "120px" }}
                            InputProps={{ inputProps: { min: 0 } }}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            type="number"
                            required
                            value={row.issue_qty || "0"}
                            onChange={(e) => {
                              handleInputChange(index, "issue_qty", e.target.value);
                              const updated = [...rowErrors];
                              updated[index] = { ...updated[index], issue_qty: "" };
                              setRowErrors(updated);
                            }}
                            error={!!rowErrors[index]?.issue_qty}
                            helperText={rowErrors[index]?.issue_qty}
                            size="small"
                            sx={{ width: "120px" }}
                            InputProps={{ inputProps: { min: 0 } }}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            type="number"
                            required
                            value={row.price || "0"}
                            onChange={(e) => {
                              handlePriceChange(index, e.target.value);
                              const updated = [...rowErrors];
                              updated[index] = { ...updated[index], price: "" };
                              setRowErrors(updated);
                            }}
                            error={!!rowErrors[index]?.price}
                            helperText={rowErrors[index]?.price}
                            size="small"
                            sx={{ width: "120px" }}
                            InputProps={{ inputProps: { min: 0, step: "any" } }}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            type="number"
                            value={row.amount || "0"}
                            disabled
                            size="small"
                            sx={{ width: "120px" }}
                            InputProps={{ inputProps: { step: "any" } }}
                          />
                        </TableCell>
                        <TableCell>
                          <Tooltip title="Delete Row">
                            <IconButton onClick={() => handleDeleteRow(index)} color="error">
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                  <TableFooter>
                    <TableRow>
                      <TableCell colSpan={8} align="right" sx={{ fontWeight: "bold" }}>
                        Total Amount:
                      </TableCell>
                      <TableCell colSpan={2} sx={{ fontWeight: "bold" }}>
                        ₹ {parseFloat(getTotalAmount()).toLocaleString()}
                      </TableCell>
                    </TableRow>
                  </TableFooter>
                </Table>
              </TableContainer>
            </>
          )}

          <Dialog open={openItemModal} onClose={handleCloseItemModal} fullWidth maxWidth="md">
            <DialogTitle>Add Item</DialogTitle>
            <DialogContent>
              <AddItemForm mode="modal" onClose={handleCloseItemModal} />
            </DialogContent>
          </Dialog>

          <Box mt={2} textAlign="center">
            <Tooltip title="Add Row">
              <IconButton onClick={handleAddRow} color="primary" size="large">
                <AddCircleOutlineIcon fontSize="large" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default IssueForm;